import "./payrollSettings.css";
import "../../companyDetails.css";
import T from "utils/constants/T";

import { useNavigate, useLocation } from "react-router";
import MainCard from "components/MainCard";
import {
  Button,
  Stack,
  TextField,
  Grid,
  Typography,
  Box,
  CircularProgress,
  InputAdornment,
  IconButton,
} from "@mui/material";
import EmployeeCostSourceSelector from "components/EmployeeCostSourceSelector";
import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchCompanyDetails } from "store/reducers/companyTabDetails";
import { get } from "lodash";
import authApi from "interceptor/authInterceptor";
import toast from "utils/ToastNotistack";
import CashDCard from "components/common/CashDCard";
import { EyeOutlined, EyeInvisibleOutlined } from "@ant-design/icons";
import AlertConfirmationModal from "components/common/AlertConfirmationModal";

interface EditPayrollSettingsTimeSiteProProps {
  companyName?: string;
  systemName?: string;
  role?: string;
  employee_cost_source?: string;
}

const CustomTextField = ({
  label,
  value,
  setter,
  errors,
  setErrors,
  errorKey,
  type = "text",
  showPassword,
  setShowPassword,
  sm,
}: any) => (
  <Grid item xs={12} sm={sm}>
    <TextField
      required
      fullWidth
      label={label}
      value={value}
      onChange={(e) => {
        const next = e.target.value;
        setter(next);
        if (errors[errorKey]) {
          setErrors((prev: any) => ({
            ...prev,
            [errorKey]: type === "password" ? "" : next ? "" : prev[errorKey],
          }));
        }
      }}
      type={type === "password" && !showPassword ? "password" : "text"}
      error={Boolean(errors[errorKey])}
      helperText={errors[errorKey]}
      InputProps={
        type === "password"
          ? {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                  </IconButton>
                </InputAdornment>
              ),
            }
          : undefined
      }
    />
  </Grid>
);

const EditPayrollSettingsTimeSitePro = ({
  companyName = "",
  systemName = "",
  employee_cost_source = "TIMESITE_PRO",
  role = "",
}: EditPayrollSettingsTimeSiteProProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const section = location.state?.section || "cost_source";
  const { selectedCompany } = useSelector(
    (state: any) => state.dashboardSearch,
  );
  const selectedCompanyId = get(selectedCompany, "_id", "");
  const { auth_myob, auth_timesite } = useSelector(
    (state: any) => state.companyTabData,
  );

  const [selectedValue, setSelectedValue] = useState<
    "TIMESITE_PRO" | "MYOB_PAYROLL"
  >(employee_cost_source as any);

  const [myobBaseUrl, setMyobBaseUrl] = useState("");
  const [myobUserName, setMyobUserName] = useState("");
  const [myobPassword, setMyobPassword] = useState("");

  const [tsCompanyName, setTsCompanyName] = useState("");
  const [tsSiteName, setTsSiteName] = useState("");
  const [tsWorkspaceName, setTsWorkspaceName] = useState("");
  const [tsApiKey, setTsApiKey] = useState("");

  const [isMyobLoading, setIsMyobLoading] = useState(false);
  const [isTsLoading, setIsTsLoading] = useState(false);
  const [showMyobPassword, setShowMyobPassword] = useState(false);
  const [showTsApiKey, setShowTsApiKey] = useState(false);

  const [myobErrors, setMyobErrors] = useState<any>({});
  const [tsErrors, setTsErrors] = useState<any>({});

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingConfirmAction, setPendingConfirmAction] = useState<
    "MYOB" | "TIMESITE" | null
  >(null);

  useEffect(() => {
    if (auth_myob) {
      setMyobBaseUrl(auth_myob.baseUrl || "");
      setMyobUserName(auth_myob.userName || "");
      setMyobPassword(auth_myob.userPassword || "");
    }
  }, [auth_myob]);

  useEffect(() => {
    if (auth_timesite) {
      setTsCompanyName(auth_timesite.companyName || "");
      setTsSiteName(auth_timesite.siteName || "");
      setTsWorkspaceName(auth_timesite.workspaceName || "");
      setTsApiKey(auth_timesite.apiKey || "");
    }
  }, [auth_timesite]);

  const handleSave = async () => {
    try {
      await authApi.put("/v2/api/timesitePro/company/employee-cost-source", {
        companyId: selectedCompanyId,
        employee_cost_source: selectedValue,
      });
      await dispatch(fetchCompanyDetails(selectedCompanyId) as any);
      toast("Employee Cost Source updated successfully", {
        variant: "success",
      });
      navigate("/companyDetails/payrollSettings/view");
    } catch (error: any) {
      toast(
        error ||
          error?.response?.data?.message ||
          error?.message ||
          "An error occurred during employee cost source update",
        { variant: "error" },
      );
      console.error(error);
    }
  };

  const handleCancel = () => navigate("/companyDetails/payrollSettings/view");

  const handleConfirm = async () => {
    if (!pendingConfirmAction) return;
    const action = pendingConfirmAction;
    setConfirmOpen(false);
    setPendingConfirmAction(null);

    try {
      if (action === "MYOB") {
        setIsMyobLoading(true);
        const saveRes = await authApi.put(
          "/v2/api/timesitePro/company/auth-myob",
          {
            companyId: selectedCompanyId,
            baseUrl: myobBaseUrl,
            userName: myobUserName,
            userPassword: myobPassword,
          },
        );
        if (saveRes?.data) {
          dispatch(fetchCompanyDetails(selectedCompanyId) as any);
          toast("MYOB credentials saved successfully", { variant: "success" });
          navigate("/companyDetails/payrollSettings/view");
        }
      } else if (action === "TIMESITE") {
        setIsTsLoading(true);
        const saveRes = await authApi.put(
          "/v2/api/timesitePro/company/auth-timesite",
          {
            companyId: selectedCompanyId,
            companyName: tsCompanyName,
            siteName: tsSiteName,
            workspaceName: tsWorkspaceName,
            apiKey: tsApiKey,
          },
        );
        if (saveRes?.data) {
          dispatch(fetchCompanyDetails(selectedCompanyId) as any);
          toast("TimeSite Pro credentials saved successfully", {
            variant: "success",
          });
          navigate("/companyDetails/payrollSettings/view");
        }
      }
    } catch (err: any) {
      toast(
        err?.response?.data?.message ||
          err?.message ||
          "An error occurred during update",
        { variant: "error" },
      );
    } finally {
      setIsMyobLoading(false);
      setIsTsLoading(false);
    }
  };

  const handleMyobSave = async () => {
    const nextErrors = {
      myobBaseUrl: !myobBaseUrl ? "Base URL is required" : "",
      myobUserName: !myobUserName ? "User Name is required" : "",
      myobPassword: !myobPassword ? "User Password is required" : "",
    };
    if (Object.values(nextErrors).some(Boolean))
      return setMyobErrors(nextErrors);

    setIsMyobLoading(true);
    try {
      const verifyRes = await authApi.post(
        "/v2/api/myobadvanced/authenticate",
        {
          authDetails: {
            baseUrl: myobBaseUrl,
            name: myobUserName,
            password: myobPassword,
          },
        },
      );
      if (verifyRes?.status === 200) {
        setPendingConfirmAction("MYOB");
        setConfirmOpen(true);
      } else {
        setIsMyobLoading(false);
      }
    } catch (err: any) {
      toast(
        err?.response?.data?.message ||
          err?.message ||
          "An error occurred during MYOB Advanced update",
        { variant: "error" },
      );
      setIsMyobLoading(false);
    }
  };

  const handleTsSave = async () => {
    const nextErrors = {
      tsCompanyName: !tsCompanyName ? "Company Name is required" : "",
      tsSiteName: !tsSiteName ? "Site Name is required" : "",
      tsWorkspaceName: !tsWorkspaceName ? "Workspace Name is required" : "",
      tsApiKey: !tsApiKey ? "API Key is required" : "",
    };
    if (Object.values(nextErrors).some(Boolean)) return setTsErrors(nextErrors);

    setIsTsLoading(true);
    try {
      const verifyRes = await authApi.post("/v2/api/timesitePro/inboard", {
        companyName: tsCompanyName,
        siteName: tsSiteName,
        workSpaceName: tsWorkspaceName,
        apiKey: tsApiKey,
      });
      if (verifyRes?.status === 200) {
        setPendingConfirmAction("TIMESITE");
        setConfirmOpen(true);
      } else {
        setIsTsLoading(false);
      }
    } catch (err: any) {
      toast(
        err?.response?.data?.message ||
          err?.message ||
          "An error occurred during TimeSite Pro update",
        { variant: "error" },
      );
      setIsTsLoading(false);
    }
  };

  const FormSection = ({ title, children, isLoading, onSave }: any) => (
    <Grid item xs={12}>
      <Box mb={1}>
        <Typography variant="h5" fontWeight="600" color="primary">
          {title}
        </Typography>
      </Box>
      <CashDCard borderRadius="12px">
        <Stack spacing={3}>
          {children}
          <Stack
            direction="row"
            spacing={2}
            justifyContent="flex-end"
            alignItems="center"
          >
            <Button
              variant="outlined"
              disabled={isLoading}
              onClick={handleCancel}
            >
              {T.CANCEL}
            </Button>
            <Button
              variant="contained"
              disabled={isLoading}
              onClick={onSave}
              startIcon={
                isLoading && <CircularProgress size={16} color="inherit" />
              }
            >
              {isLoading ? "Verifying & Saving..." : T.SAVE}
            </Button>
          </Stack>
        </Stack>
      </CashDCard>
    </Grid>
  );

  return (
    <MainCard title={`Edit ${systemName}`}>
      <AlertConfirmationModal
        open={confirmOpen}
        onClose={() => {
          setConfirmOpen(false);
          setPendingConfirmAction(null);
          setIsMyobLoading(false);
          setIsTsLoading(false);
        }}
        onConfirm={handleConfirm}
        title="Are you sure you want to change this?"
      >
        <Typography variant="body2">
          After confirmation,{" "}
          {pendingConfirmAction === "MYOB" ? "MYOB" : "TimeSite Pro"}{" "}
          credentials will be updated.
        </Typography>
      </AlertConfirmationModal>

      <Grid container spacing={4}>
        {section === "cost_source" && (
          <Grid item xs={12}>
            <Box mb={1}>
              <Typography variant="h5" fontWeight="600" color="primary">
                Employee Cost Source
              </Typography>
            </Box>
            <CashDCard borderRadius="12px">
              <Stack spacing={3}>
                <EmployeeCostSourceSelector
                  value={selectedValue}
                  onChange={setSelectedValue}
                />
                <Stack direction="row" spacing={2} justifyContent="flex-end">
                  <Button variant="outlined" onClick={handleCancel}>
                    {T.CANCEL}
                  </Button>
                  <Button variant="contained" onClick={handleSave}>
                    {T.SAVE}
                  </Button>
                </Stack>
              </Stack>
            </CashDCard>
          </Grid>
        )}

        {section === "myob" && (
          <FormSection
            title="MYOB Advanced Credentials"
            isLoading={isMyobLoading}
            onSave={handleMyobSave}
          >
            <Grid container spacing={2}>
              <CustomTextField
                label="Base URL"
                value={myobBaseUrl}
                setter={setMyobBaseUrl}
                errors={myobErrors}
                setErrors={setMyobErrors}
                errorKey="myobBaseUrl"
                sm={4}
              />
              <CustomTextField
                label="User Name"
                value={myobUserName}
                setter={setMyobUserName}
                errors={myobErrors}
                setErrors={setMyobErrors}
                errorKey="myobUserName"
                sm={4}
              />
              <CustomTextField
                label="User Password"
                value={myobPassword}
                setter={setMyobPassword}
                errors={myobErrors}
                setErrors={setMyobErrors}
                errorKey="myobPassword"
                sm={4}
                type="password"
                showPassword={showMyobPassword}
                setShowPassword={setShowMyobPassword}
              />
            </Grid>
          </FormSection>
        )}

        {section === "timesite" && (
          <FormSection
            title="TimeSite Pro Credentials"
            isLoading={isTsLoading}
            onSave={handleTsSave}
          >
            <Grid container spacing={2}>
              <CustomTextField
                label="Company Name"
                value={tsCompanyName}
                setter={setTsCompanyName}
                errors={tsErrors}
                setErrors={setTsErrors}
                errorKey="tsCompanyName"
                sm={3}
              />
              <CustomTextField
                label="Site Name"
                value={tsSiteName}
                setter={setTsSiteName}
                errors={tsErrors}
                setErrors={setTsErrors}
                errorKey="tsSiteName"
                sm={3}
              />
              <CustomTextField
                label="Workspace Name"
                value={tsWorkspaceName}
                setter={setTsWorkspaceName}
                errors={tsErrors}
                setErrors={setTsErrors}
                errorKey="tsWorkspaceName"
                sm={3}
              />
              <CustomTextField
                label="API Key"
                value={tsApiKey}
                setter={setTsApiKey}
                errors={tsErrors}
                setErrors={setTsErrors}
                errorKey="tsApiKey"
                sm={3}
                type="password"
                showPassword={showTsApiKey}
                setShowPassword={setShowTsApiKey}
              />
            </Grid>
          </FormSection>
        )}
      </Grid>
    </MainCard>
  );
};

export default EditPayrollSettingsTimeSitePro;
